'use client';

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSession } from 'next-auth/react';
import { BELL_REFRESH_EVENT } from '@/lib/bell-refresh';

export interface EmailNotificationItem {
  id: string;
  emailType: string;
  subject: string;
  body?: string;
  status: 'sent' | 'failed';
  sentAt: string;
  read: boolean;
  readAt?: string;
}

interface EmailNotificationContextValue {
  notifications: EmailNotificationItem[];
  unreadCount: number;
  loading: boolean;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  refresh: () => Promise<void>;
}

const EmailNotificationContext = createContext<EmailNotificationContextValue>({
  notifications: [],
  unreadCount: 0,
  loading: true,
  markRead: async () => {},
  markAllRead: async () => {},
  refresh: async () => {},
});

const POLL_INTERVAL_MS = 10 * 60 * 1000; // 10 min, matches SignalFeedContext's general poll

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
  });
  if (!res.ok) throw new Error(`Request to ${path} failed with ${res.status}`);
  const json = await res.json();
  return json.data as T;
}

export function EmailNotificationProvider({ children }: { children: React.ReactNode }) {
  const { data: session, status } = useSession();
  const [notifications, setNotifications] = useState<EmailNotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchAll = useCallback(async () => {
    try {
      const [listData, countData] = await Promise.all([
        apiFetch<{ notifications: EmailNotificationItem[] }>('/api/notifications?limit=20'),
        apiFetch<{ unreadCount: number }>('/api/notifications/unread-count'),
      ]);
      setNotifications(listData.notifications);
      setUnreadCount(countData.unreadCount);
    } catch {
      // Silent — the bell simply shows stale/empty data until the next poll.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (status !== 'authenticated') return;
    fetchAll();

    pollRef.current = setInterval(() => {
      if (!document.hidden) fetchAll();
    }, POLL_INTERVAL_MS);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [status, fetchAll]);

  // Real-time system notifications (task assigned, ...) arrive over the Signal
  // Feed's SSE stream; that context asks the bell to refetch (GitHub #1054).
  useEffect(() => {
    if (status !== 'authenticated') return;
    const onRefresh = () => {
      fetchAll();
    };
    window.addEventListener(BELL_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(BELL_REFRESH_EVENT, onRefresh);
  }, [status, fetchAll]);

  const markRead = useCallback(async (id: string) => {
    setNotifications(prev => prev.map(n => (n.id === id ? { ...n, read: true } : n)));
    setUnreadCount(prev => Math.max(0, prev - 1));
    try {
      await apiFetch(`/api/notifications/${id}/read`, { method: 'POST' });
    } catch {
      fetchAll();
    }
  }, [fetchAll]);

  const markAllRead = useCallback(async () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
    try {
      await apiFetch('/api/notifications/mark-all-read', { method: 'POST' });
    } catch {
      fetchAll();
    }
  }, [fetchAll]);

  return (
    <EmailNotificationContext.Provider
      value={{ notifications, unreadCount, loading, markRead, markAllRead, refresh: fetchAll }}
    >
      {children}
    </EmailNotificationContext.Provider>
  );
}

export function useEmailNotifications() {
  return useContext(EmailNotificationContext);
}
